import {
    createAsyncThunk,
    createSelector,
    createSlice,
} from '@reduxjs/toolkit'
import {
    api, auth, Jwt
} from 'utils'
import { RootState } from 'store'
import {
    CreateJwtTokenObtainPairRequest,
    RetrieveUserResponse
} from 'types/swagger'
import { RequestStatus } from 'types'
import { MyOrganizationFragment } from 'generated/graphql'
import { getMyOrganizations } from 'features/events/graphql/query'
import { activateProfile } from '../profile/editProfileSlice'

export interface AuthState {
    readonly authRequestState: RequestStatus
    readonly isLoadingAuth: boolean
    readonly isLoggedIn: boolean
    readonly user?: RetrieveUserResponse
    myOrganizations?: MyOrganizationFragment[]
    myOrganizationsStatus?: RequestStatus
    organizationLanguage?: string
}

const initAuthState: AuthState = {
    isLoadingAuth: true,
    authRequestState: 'idle',
    isLoggedIn: false,
    user: undefined,
    myOrganizations: [],
    myOrganizationsStatus: 'idle',
    organizationLanguage: undefined,
}

type LoginParams = CreateJwtTokenObtainPairRequest
export const login = createAsyncThunk(
    'LOGIN',
    async (params: LoginParams, thunkAPI) => {
        await auth.login(params)
        thunkAPI.dispatch(getCurrentUser())
    }
)

export const activateUser = createAsyncThunk(
    'ACTIVATE_USER',
    async ({ activationKey }: { activationKey }) => {
        const user = { terms_agreed: true }
        const data = { activation_key: activationKey, user }
        const response = await api
            .put<Jwt & { user: RetrieveUserResponse }>('/profiles/activate/', data, { disableAuth: true })
        auth.storeTokens(response)
        return response
    },
)

export const getCurrentUser = createAsyncThunk(
    'CURRENT_USER_ACTION',
    async (param, thunkApi) => {
        try {
            const response = await api.get<RetrieveUserResponse>('/auth/current_user/')
            if (!response.first_name || !response.last_name) {
                thunkApi.dispatch(activateProfile())
            }

            return response
        } catch (err) {
            return thunkApi.rejectWithValue(err)
        }
    }
)

export const fetchMyOrganization = createAsyncThunk(
    'GET_MY_ORGANIZATION',
    async () => getMyOrganizations()
)

const setUser = (state: AuthState, action): AuthState => (!action.payload?.id
    ? onUnauthenticated()
    : ({
        isLoadingAuth: false,
        authRequestState: 'succeeded',
        isLoggedIn: true,
        user: action.payload,
    }))

const onUnauthenticated = (): AuthState => ({
    isLoadingAuth: false,
    authRequestState: 'failed',
    isLoggedIn: false,
    user: undefined,
    myOrganizations: [],
    myOrganizationsStatus: 'idle',
})

const authSlice = createSlice({
    name: 'auth',
    initialState: initAuthState,
    reducers: {
        logout: () => {
            auth.logout()
            return onUnauthenticated()
        },
        editUser: (state, { payload }) => {
            state.user = { ...state.user, ...payload }
        },
        updateOrganizationLanguage: (state, { payload }) => {
            state.organizationLanguage = payload
        }
    },
    extraReducers: (builder) => {
        builder.addCase(getCurrentUser.fulfilled, (state, action) => (
            setUser(state, action)
        ))
        builder.addCase(getCurrentUser.rejected, () => onUnauthenticated())
        builder.addCase(activateUser.fulfilled, (state, action) => {
            state.isLoggedIn = true
            state.isLoadingAuth = false
            state.authRequestState = 'succeeded'
            state.user = action.payload.user
        })
        builder.addCase(fetchMyOrganization.fulfilled, (state, { payload }) => {
            state.myOrganizations = payload
            state.myOrganizationsStatus = 'succeeded'
        })
        builder.addCase(fetchMyOrganization.pending, (state) => {
            state.myOrganizationsStatus = 'loading'
        })
        builder.addCase(fetchMyOrganization.rejected, (state) => {
            state.myOrganizationsStatus = 'failed'
        })
    },
})

export const currentUserSelector = createSelector(
    [(state: RootState) => state.auth],
    ({ user }) => user,
)

export const isAuthenticatedSelector = createSelector(
    [(state: RootState) => state.auth],
    (_auth) => _auth.isLoggedIn,
)

export const authRequestStateSelector = createSelector(
    [(state: RootState) => state.auth],
    (_auth) => _auth.authRequestState,
)
export const myOrganizationsSelector = (state: RootState) => state.auth.myOrganizations
export const myOrganizationsStatusSelector = (state: RootState) => state.auth.myOrganizationsStatus
export const isOrganizationAdminSelector = (organizationId) => (state: RootState): boolean => {
    if (!state.auth.myOrganizations) return false

    return !!state.auth.myOrganizations.find((org) => org.id === +organizationId)
}
export const organizationLanguageSelector = (state: RootState) => state.auth.organizationLanguage

export const logout = authSlice.actions.logout
export const updateCurrentUser = authSlice.actions.editUser
export const updateOrganizationLanguage = authSlice.actions.updateOrganizationLanguage

export default authSlice.reducer
